import { useState } from 'react';
import { faMinus, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { tv } from 'tailwind-variants';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { EntityRatingComment } from '@medprev-web-app/api-graphql-client';

import { EntityCommentSection } from './entity-comment-section.component';
import { useTextOverflow } from './hooks/use-text-overflow';

import { Star } from '@/components/ui/star.component';
import { formatISO8601toBRDate } from '@/utils/formatters/format-iso-8601-to-br-date';
import { formatToTitleCase } from '@/utils/formatters/format-to-title-case';
import { Button } from '@/components/ui/button/button.component';
import { formatDateToBR } from '@/utils/formatters/date';
import { AnalyticsEnumKeyType } from '@/types/enums/analytics/analytics.enum';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';

interface EntityCommentProps {
  lastItem?: boolean;
  comment: EntityRatingComment;
  index: number;
  ['data-analytics-event']?: AnalyticsEnumKeyType;
}

const commentStyle = tv({
  slots: {
    container: ['overflow-hidden transition-all duration-500 ease-in-out'],
    text: ['text-gray-700 line-clamp-5'],
  },
  variants: {
    showMore: {
      true: {
        text: ['line-clamp-none'],
      },
    },
  },
});

export function EntityComment({
  comment,
  lastItem,
  index,
  'data-analytics-event': analyticsEventName,
}: EntityCommentProps) {
  const analytics = getAnalyticsPlugin();

  const splittedName = comment.authorName.split(' ');
  const name = splittedName[0] || comment.authorName;

  const [showMore, setShowMore] = useState<boolean>(false);
  const [showMoreResponse, setShowMoreResponse] = useState<boolean>(false);

  const { container, text } = commentStyle();
  const { textRef, hasOverflow, collapsedHeight, expandedHeight } = useTextOverflow();
  const { textRef: responseTextRef, hasOverflow: hasResponseOverflow, collapsedHeight: responseCollapsedHeight, expandedHeight: responseExpandedHeight } = useTextOverflow();

  const containerHeight = (hasOverflow || showMore)
    ? (showMore ? expandedHeight : collapsedHeight)
    : undefined;

  const responseContainerHeight = (hasResponseOverflow || showMoreResponse)
    ? (showMoreResponse ? responseExpandedHeight : responseCollapsedHeight)
    : undefined;

  if (!comment || !comment.authorName) {
    return null;
  }

  return (
    <EntityCommentSection
      title={formatToTitleCase(name)}
      removeHr={lastItem}
      data-testid={`${DataTestIdEnum.ENTITY_COMMENT_SESSION}-${index}`}
    >
      <div
        className="flex items-center gap-1"
        data-testid={`${DataTestIdEnum.ENTITY_COMMENT_CONTENT}-${index}`}
      >
        <Star
          rating={comment.ratingValue || 0}
          dataTestId={`${DataTestIdEnum.ENTITY_COMMENT_STAR}-${index}`}
        />
        {comment.createdAt && (
          <span
            className="text-gray-500"
            data-testid={`${DataTestIdEnum.ENTITY_COMMENT_DATE}-${index}`}
          >
            {formatISO8601toBRDate(comment.createdAt)}
          </span>
        )}
      </div>

      <div className="mt-4">
        <div
          style={containerHeight ? { height: `${containerHeight}px` } : undefined}
          className={container()}
          data-testid={`${DataTestIdEnum.ENTITY_COMMENT_TEXT}-${index}`}
        >
          <p
            ref={textRef}
            className={text({ showMore })}
            style={{ wordBreak: 'break-word', whiteSpace: 'pre-line' }}
          >
            {comment.comment}
          </p>
        </div>

        {(hasOverflow || showMore) && (
          <Button
            data-testid={`${DataTestIdEnum.ENTITY_BUTTON_SHOW_MORE_COMMENT}-${index}`}
            variant="text"
            startIcon={showMore ? faMinus : faPlus}
            onClick={() => {
              if (!showMore) {
                analytics.track(analyticsEventName as string);
              }
              setShowMore((prev) => !prev);
            }}
            startIconClassName={`w-[0.875rem] h-[0.875rem] transition-transform duration-300 ease-in-out z-0 ${showMore ? 'rotate-180' : 'rotate-0'}`}
            className="mt-2 text-green-500 hover:underline focus:outline-none transition-colors duration-300 ease-in-out text-left justify-start"
          >
            {showMore ? 'Recolher' : 'Ver mais'}
          </Button>
        )}
      </div>

      {comment.responseComment && (
        <div className="mt-4 border-l-2 pl-4 border-blue-100">
          <div className="mb-2">
            <p className="text-gray-800 font-medium">
              Resposta do estabelecimento
            </p>
            <p className="text-gray-500 font-normal">
              {formatDateToBR(new Date(comment.responseComment.createdAt))}
            </p>
          </div>
          <div
            style={responseContainerHeight ? { height: `${responseContainerHeight}px` } : undefined}
            className={container()}
          >
            <div
              ref={responseTextRef}
              className={text({ showMore: showMoreResponse })}
              style={{ wordBreak: 'break-word', whiteSpace: 'pre-line' }}
              data-testid={`${DataTestIdEnum.ENTITY_RESPONSE_COMMENT_TEXT}-${index}`}
            >
              {comment.responseComment.comment}
            </div>
          </div>

          {(hasResponseOverflow || showMoreResponse) && (
            <Button
              variant="text"
              startIcon={showMoreResponse ? faMinus : faPlus}
              onClick={() => setShowMoreResponse((prev) => !prev)}
              startIconClassName={`w-[0.875rem] h-[0.875rem] transition-transform duration-300 ease-in-out z-0 ${showMoreResponse ? 'rotate-180' : 'rotate-0'}`}
              className="mt-2 text-green-500 hover:underline focus:outline-none transition-colors duration-300 ease-in-out text-left justify-start"
            >
              {showMoreResponse ? 'Recolher' : 'Ver mais'}
            </Button>
          )}
        </div>
      )}
    </EntityCommentSection>
  );
}
